import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  Input,
  Box,
  FormMessage,
} from "@orion/ui";

import { useTranslation } from "react-i18next";

import { FC, useRef } from "react";
import { UseFormReturn } from "react-hook-form";
import ReCAPTCHA from "react-google-recaptcha";
import { LoginResetPswFormType } from "@delorean/login/schemas/login";
import { LoginResetPswType } from "../types";
import { CaptchaActionEnum, useCaptcha } from "@delorean/_shared/hooks/useCaptcha";

type Props = {
  form: UseFormReturn<LoginResetPswFormType>;
  onSubmitForm: (values: LoginResetPswType) => void;
};

const IdentityFormResetPsw: FC<Props> = ({ form, onSubmitForm }) => {
  const { t } = useTranslation();
  const contractCodeRef = useRef<HTMLInputElement | null>(null);
  const userCodeRef = useRef<HTMLInputElement | null>(null);
  const fiscalCodeRef = useRef<HTMLInputElement | null>(null);
  const { getCaptchaToken } = useCaptcha();

  const handleSubmitWithCaptcha = async (data: LoginResetPswFormType) => {
    try {
      const token = await getCaptchaToken(CaptchaActionEnum.RESET_PASSWORD);
      onSubmitForm({ ...data, captcha: token });
    } catch (error) {
      if (error && typeof error === "object" && "message" in error) {
        throw new Error((error as { message: string }).message);
      } else {
        throw new Error("An unknown captcha error occurred");
      }
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmitWithCaptcha)}>
        <Box
          my15
          flex
          flexDirection="row"
          gap15
          style={{
            width: "576px",
            minHeight: "44px",
            padding: "0 8px",
          }}>
          {/* Contract Code */}
          <FormField
            control={form.control}
            name="contractCode"
            render={({ field }) => (
              <FormItem style={{ width: "272px" }}>
                <FormControl>
                  <Input
                    {...field}
                    ref={(el) => {
                      field.ref(el);
                      contractCodeRef.current = el;
                    }}
                    aria-label={t("accessibility.contractCode")}
                    label={t("login.form.contractCode")}
                    placeholder={t("login.form.contractCode")}
                    aria-required="true"
                    aria-invalid={!!form.formState.errors.contractCode}
                    aria-describedby="contractCode-error"
                  />
                </FormControl>
                {/* <FormMessage id="contractCode-error" /> */}
              </FormItem>
            )}
          />

          {/* User Code */}
          <FormField
            control={form.control}
            name="userCode"
            render={({ field }) => (
              <FormItem style={{ width: "272px" }}>
                <FormControl>
                  <Input
                    {...field}
                    ref={(el) => {
                      field.ref(el);
                      userCodeRef.current = el;
                    }}
                    aria-label={t("accessibility.userCode")}
                    label={t("login.form.userCode")}
                    placeholder={t("login.form.userCode")}
                    aria-required="true"
                    aria-invalid={!!form.formState.errors.userCode}
                    aria-describedby="userCode-error"
                  />
                </FormControl>
                {/* <FormMessage id="userCode-error" /> */}
              </FormItem>
            )}
          />
        </Box>

        {/* Fiscal code */}
        <Box
          style={{
            width: "576px",
            minHeight: "44px",
            padding: "0 8px",
          }}
        >
          <FormField
            control={form.control}
            name="fiscalCode"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    {...field}
                    ref={(el) => {
                      field.ref(el);
                      fiscalCodeRef.current = el;
                    }}
                    aria-label={t("accessibility.fiscalCode")}
                    placeholder={t("forgetPassword.form.fiscalCode")}
                    label={t("forgetPassword.form.fiscalCode")}
                    aria-required="true"
                    aria-invalid={!!form.formState.errors.fiscalCode}
                    aria-describedby="codiceFiscale-error"
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </Box>
        <Box justifyItems="center">
          <Button type="submit" minW52 mt10>
            {t("forgetPassword.submit")}
          </Button>
        </Box>
      </form>
    </Form>
  );
};

export default IdentityFormResetPsw;
